import { isDefined } from "@clipboard-health/util-ts";
import { type ComponentType, lazy, type LazyExoticComponent } from "react";
import { type ConnectedComponent } from "react-redux";
import retry from "async-retry";
import { logError } from "@src/appV2/lib/analytics";

export interface LazyChunkLoadRecoveryContext {
  appVersion: string;
  commitHash: string;
  href: string;
  reload: () => void;
  sessionStorage?: Pick<Storage, "getItem" | "setItem">;
}

const LAZY_CHUNK_LOAD_RECOVERY_STORAGE_PREFIX = "cbh:lazy-chunk-load-recovery";
const LAZY_CHUNK_LOAD_RECOVERY_STORAGE_VALUE = "reloaded";
const UNKNOWN_BUILD_VALUE = "unknown";

const RECOVERABLE_LAZY_CHUNK_ERROR_MESSAGES = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "Loading chunk",
  "Unable to preload CSS for",
] as const;

// in order to mimic what React.lazy does, we use the same function's signature
// https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/react/index.d.ts#L881
export function loadLazily<
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<unknown> | ConnectedComponent<any, any> | React.FC<any>,
>(factory: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await retry(factory, {
        retries: 2,
        minTimeout: 200,
      });
    } catch (error) {
      handleLazyChunkLoadFailure(error);
      throw error;
    }
  });
}

export function handleLazyChunkLoadFailure(
  error: unknown,
  context: LazyChunkLoadRecoveryContext = getLazyChunkLoadRecoveryContext()
): void {
  const shouldReload = shouldRecoverFromLazyChunkLoadFailure(error, context);
  const recoveryAction = shouldReload ? "reload" : "error-boundary";

  logError("Lazy chunk load failure", {
    error,
    metadata: getLazyChunkLoadFailureMetadata({ context, recoveryAction }),
  });

  if (!shouldReload) {
    return;
  }

  try {
    context.reload();
  } catch (reloadError) {
    logError("Lazy chunk reload failure", {
      error: reloadError,
      metadata: getLazyChunkLoadFailureMetadata({ context, recoveryAction: "reload-failed" }),
    });
  }
}

export function shouldRecoverFromLazyChunkLoadFailure(
  error: unknown,
  context: LazyChunkLoadRecoveryContext = getLazyChunkLoadRecoveryContext()
): boolean {
  if (!isRecoverableLazyChunkLoadError(error)) {
    return false;
  }

  if (!isDefined(context.sessionStorage)) {
    return false;
  }

  const storageKey = getLazyChunkLoadRecoveryStorageKey(context);

  try {
    if (context.sessionStorage.getItem(storageKey) === LAZY_CHUNK_LOAD_RECOVERY_STORAGE_VALUE) {
      return false;
    }

    context.sessionStorage.setItem(storageKey, LAZY_CHUNK_LOAD_RECOVERY_STORAGE_VALUE);
    return true;
  } catch {
    return false;
  }
}

export function getLazyChunkLoadRecoveryContext(): LazyChunkLoadRecoveryContext {
  return {
    appVersion: getAppVersion(),
    commitHash: getCommitHash(),
    href: window.location.href,
    reload: () => window.location.reload(),
    sessionStorage: getSessionStorage(),
  };
}

function isRecoverableLazyChunkLoadError(error: unknown): boolean {
  const errorMessage = getErrorMessage(error);

  if (error instanceof Error && error.name === "ChunkLoadError") {
    return true;
  }

  return RECOVERABLE_LAZY_CHUNK_ERROR_MESSAGES.some((message) => errorMessage.includes(message));
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

function getLazyChunkLoadRecoveryStorageKey(context: LazyChunkLoadRecoveryContext): string {
  return [LAZY_CHUNK_LOAD_RECOVERY_STORAGE_PREFIX, context.appVersion, context.commitHash].join(
    ":"
  );
}

interface LazyChunkLoadFailureMetadataParams {
  context: LazyChunkLoadRecoveryContext;
  recoveryAction: string;
}

function getLazyChunkLoadFailureMetadata({
  context,
  recoveryAction,
}: LazyChunkLoadFailureMetadataParams): Record<string, unknown> {
  return {
    appVersion: context.appVersion,
    commitHash: context.commitHash,
    recoveryAction,
    url: context.href,
  };
}

function getAppVersion(): string {
  if (typeof __APP_VERSION__ === "string") {
    return __APP_VERSION__;
  }

  return UNKNOWN_BUILD_VALUE;
}

function getCommitHash(): string {
  if (typeof __COMMIT_HASH__ === "string") {
    return __COMMIT_HASH__;
  }

  return UNKNOWN_BUILD_VALUE;
}

function getSessionStorage(): Pick<Storage, "getItem" | "setItem"> | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}
